/** Finds the issue/revision date printed in a safety data sheet so the five-year review date can be set. */
export interface FoundDate { date: Date; label: string; statesFiveYearValidity: boolean }

const MONTH = '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)';
const DATE_VALUE = `(\\d{1,2}[\\/.\\-]\\d{1,2}[\\/.\\-]\\d{2,4}|\\d{4}[\\/.\\-]\\d{1,2}[\\/.\\-]\\d{1,2}|\\d{1,2}\\s+${MONTH}\\s+\\d{4}|${MONTH}\\s+\\d{1,2},?\\s+\\d{4})`;
const LABELS = [
  { expression: /(?:date\s+(?:of\s+)?revision|revision\s+date|revised\s+date|last\s+revised|date\s+revised|revision\s*(?:no\.?|number)?)/gi, priority: 4 },
  { expression: /(?:version\s*(?:issued|date|revised|revision)|version\s*(?:no\.?|number)?\s*[:#-]?\s*(?:issued|dated))/gi, priority: 3 },
  { expression: /(?:date\s+(?:of\s+)?issue|issue(?:d)?\s+date|date\s+issued|date\s+prepared|prepared\s+on|effective\s+date)/gi, priority: 2 },
  { expression: /(?:valid(?:ity)?\s+(?:date|from)|valid\s+for\s+(?:five|5)\s+years?\s+from)/gi, priority: 1 }
];

export function parseDocumentDate(value: string): Date | null {
  const cleaned = value.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
  let match = cleaned.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (match) {
    // Australian documents write day first.
    const year = Number(match[3].length === 2 ? `20${match[3]}` : match[3]);
    const date = new Date(Date.UTC(year, Number(match[2]) - 1, Number(match[1])));
    return date.getUTCFullYear() === year && date.getUTCMonth() === Number(match[2]) - 1 && date.getUTCDate() === Number(match[1]) ? date : null;
  }
  match = cleaned.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (match) return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const parsed = new Date(`${cleaned} UTC`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function findIssuedDate(text: string): FoundDate | null {
  const candidates: Array<{ date: Date; label: string; priority: number; index: number }> = [];
  for (const { expression, priority } of LABELS) {
    expression.lastIndex = 0;
    let label: RegExpExecArray | null;
    while ((label = expression.exec(text))) {
      const tail = text.slice(label.index + label[0].length, label.index + label[0].length + 120);
      const value = tail.match(new RegExp(`^[\\s:,-]*(?:version\\s*(?:no\\.?|number)?\\s*[\\w.]+[\\s:,-]*)?${DATE_VALUE}`, 'i'));
      if (!value) continue;
      const date = parseDocumentDate(value[1]);
      if (date) candidates.push({ date, label: label[0], priority, index: label.index });
    }
  }
  candidates.sort((a, b) => b.priority - a.priority || a.index - b.index);
  const found = candidates[0];
  return found ? { date: found.date, label: found.label, statesFiveYearValidity: /(?:valid\s+for\s+(?:five|5)\s+years?|validity\s+(?:period|of)\s+(?:five|5)\s+years?)/i.test(text) } : null;
}

async function readPdfText(file: Blob): Promise<string> {
  // Loaded on demand: pdf.js is large and only needed when an SDS is uploaded. Bundled, so it works offline.
  const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  let text = '';
  for (let page = 1; page <= Math.min(pdf.numPages, 12); page += 1) {
    const content = await (await pdf.getPage(page)).getTextContent();
    text += ` ${content.items.map((item) => ('str' in item ? item.str : '')).join(' ')}`;
  }
  return text;
}

export async function scanSdsDate(file: File): Promise<FoundDate | null> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  return findIssuedDate(isPdf ? await readPdfText(file) : await file.text());
}

export const isoDate = (date: Date) => date.toISOString().slice(0, 10);
export function fiveYearsAfter(date: Date) {
  const next = new Date(date);
  next.setUTCFullYear(next.getUTCFullYear() + 5);
  return next;
}

/** Builds the native date-input value for an SDS review due five years after its issue or revision date. */
export function reviewDateForIssuedDate(issuedDate: string): string {
  const issued = parseDocumentDate(issuedDate);
  return issued ? isoDate(fiveYearsAfter(issued)) : '';
}

/** Auto-filled dates may be updated; manually chosen dates must always be preserved. */
export function shouldUpdateAutofilledReviewDate(currentReviewDate: string, wasAutoFilled: boolean): boolean {
  return !currentReviewDate || wasAutoFilled;
}

/** A scan result may only update the form when no later file choice or manual edit superseded it. */
export const isCurrentSdsScan = (scanId: number, currentScanId: number) => scanId === currentScanId;
